import { useState, useEffect } from "react";
import { Container, Typography, Paper, Button } from "@mui/material";
import { SectionQuestions } from "./components/SectionQuestions";
import { useFormFields } from "./hooks";
import { useParams } from "react-router-dom";
import {
	getUniqueOrderedSections,
	filterQuestionBySection,
	orderQuestionsByItemOrder,
	shouldQuestionRender,
	webFormSubmitResponse,
	getQuestions,
} from "./libs";
import { useAppContext } from "../../Libs/contextLib";
import { questionType, ResponseError, ResponseResult } from "./types";

type WebFormParams = {
	formId: string;
};

export const WebForm = () => {
	const { currUser } = useAppContext();
	const { formId } = useParams<WebFormParams>();

	const [fields, handleFieldChange, resetFields] = useFormFields();
	const [errorFields, setErrorFields] = useState([]);
	const [questionSet, setQuestionSet] = useState([]);

	useEffect(() => {
		onLoad();
	}, []);

	const onLoad = async () => {
		const result = await getQuestions({
			formId: formId,
		});
		const questions = result.Questions;
		setQuestionSet(questions);
		const parsedQuestions = defaultQuestionValues(questions);
		resetFields(parsedQuestions);
	};

	const defaultQuestionValues = (questions: questionType[]) =>
		questions.reduce(
			(addedFields, currentQuestion) => ({
				...addedFields,
				[currentQuestion.name]: currentQuestion.initialText || "",
			}),
			{}
		);

	const sections = getUniqueOrderedSections(questionSet);

	const getOrderedQuestionsForSection = (section: string) => {
		return orderQuestionsByItemOrder(
			filterQuestionBySection(questionSet, section)
		);
	};

	const getDisplayedQuestions = () => {
		return questionSet.filter((questionData) => {
			return shouldQuestionRender(questionData, fields);
		});
	};

	const getDataToValidate = (questions: questionType[]) => {
		return questions.map((questionData) => {
			return {
				name: questionData.name,
				value: fields[questionData.name],
				required: questionData.required,
				validation: questionData.validation,
			};
		});
	};

	const getDataToSubmit = (questions: questionType[]) => {
		return questions.map((questionData) => {
			return {
				name: questionData.name,
				question: questionData.question,
				value: fields[questionData.name],
			};
		});
	};

	const getAllErroredFields = (fields: any): string[] => {
		return fields.filter(isFieldInvalid).map((field: any) => field.name);
	};

	const isFieldInvalid = (field: any): boolean => {
		const { required, value, validation } = field;
		if (required && !value) return true;
		if (validation && new RegExp(validation).test(value) === false) return true;
		return false;
	};

	const handleSubmit = async (event: any) => {
		event.preventDefault();
		const displayedQuestions = getDisplayedQuestions();
		const dataToValidate = getDataToValidate(displayedQuestions);
		const isValidResponse = getAllErroredFields(dataToValidate).length === 0;
		if (isValidResponse) {
			const dataToSubmit = getDataToSubmit(displayedQuestions);
			setErrorFields([]);
			const params = {
				response: dataToSubmit,
				formId: questionSet[0].formId,
				markedBy: `${currUser.attributes.given_name} ${currUser.attributes.family_name}`,
			};
			const result = await webFormSubmitResponse(params);
			handleResponseSubmitResult(result);
		} else {
			alert("Please check missing / invalid fields");
			setErrorFields(getAllErroredFields(dataToValidate));
			return;
		}
	};

	const handleResponseSubmitResult = (
		result: ResponseResult | ResponseError
	) => {
		if (resultIsError(result)) {
			handleResultError(result);
			return;
		}
		alert("Response Submitted");
		resetFields(defaultQuestionValues(questionSet));
	};

	const resultIsError = (result: any): result is ResponseError => {
		return result.error;
	};

	const handleResultError = (result: ResponseError) => {
		alert("Sorry, something went wrong");
		console.error(result.error);
	};

	return (
		<Container maxWidth="lg">
			{!fields && <Typography variant="subtitle1">Loading...</Typography>}
			{fields && (
				<>
					<Typography variant="h3">{questionSet[0]?.formName}</Typography>
					<Paper
						sx={{
							padding: 1,
							marginBottom: 4,
							display: "flex",
							flexDirection: "column",
						}}
						elevation={3}
						component="form"
						noValidate
						autoComplete="off"
					>
						{fields &&
							sections.map((section) => {
								return (
									<SectionQuestions
										key={section}
										section={section}
										questions={getOrderedQuestionsForSection(section)}
										fields={fields}
										handleOnChange={handleFieldChange}
										errorFields={errorFields}
									/>
								);
							})}
						<Button
							variant="contained"
							sx={{ alignSelf: "flex-end" }}
							type="submit"
							onClick={handleSubmit}
						>
							Submit
						</Button>
					</Paper>
				</>
			)}
		</Container>
	);
};
